// src/features/data/entity-groups-cell.tsx
// IR-1045 Phase 3 (Entities tab) — the "Groups" cell.
// The entities_list payload carries only `group_count` (a number); group
// NAMES live on the detail endpoint (`group_memberships[].group_name`),
// deliberately omitted from the list path to avoid an N+1 across the page.
// So we lazy-fetch on hover: the count is the tooltip trigger; opening the
// tooltip enables a `useEntity(id)` detail fetch (react-query cached) and
// lists the group names. Mirrors features/links/SourceGroupsCell.tsx.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEntity } from "@/hooks/use-data";
import type { EntityDetail } from "@/lib/types";
import { useState } from "react";

interface Props {
  entityId: string;
  groupCount: number;
}

export function EntityGroupsCell({ entityId, groupCount }: Props) {
  // Only fetch once the tooltip has been opened at least once. `useEntity` is
  // gated on a non-null id, so passing null keeps it idle until first hover.
  const [opened, setOpened] = useState(false);
  const { data, isLoading } = useEntity(opened ? entityId : null);
  const detail = data as EntityDetail | undefined;
  const memberships = detail?.group_memberships ?? [];

  if (groupCount <= 0) {
    return <span style={{ color: "var(--iris-fg-5)" }}>0</span>;
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip onOpenChange={(open) => open && setOpened(true)}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Show the ${groupCount} groups for this entity`}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "var(--iris-fg-3)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline dotted",
              textUnderlineOffset: 2,
              font: "inherit",
            }}
          >
            {groupCount}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px]">
          {isLoading ? (
            <span style={{ color: "var(--iris-fg-4)" }}>Loading groups…</span>
          ) : memberships.length === 0 ? (
            <span style={{ color: "var(--iris-fg-4)" }}>No group names available</span>
          ) : (
            <ul
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                margin: 0,
                padding: 0,
                listStyle: "none",
              }}
            >
              {memberships.map((m) => (
                <li
                  key={m.id}
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {m.group_name ?? `#${m.id}`}
                </li>
              ))}
            </ul>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
